import {catchPromise} from '@hconnect/common/catchPromise'
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  createContext,
  useContext,
  ReactNode
} from 'react'

import {getUser, getLoginState, startLogoutProcess} from './api/endpoints'
import {useUserContext} from './UserContextProvider'

export interface LoginContextState {
  isLoggedIn: boolean
  logout: () => Promise<void>
}

export const LoginContext = createContext<LoginContextState | undefined>(undefined)

export const useLoginContext = (): LoginContextState => {
  const context = useContext(LoginContext)
  if (!context) {
    throw new Error('useLoginContext must be used within a LoginProvider')
  }
  return context
}

export interface LoginProviderProps {
  children: ReactNode
}

/**
 * Core LoginProvider - manages authentication state and login flow
 * Requires UserContextProvider to be present in the component tree
 */
export const LoginProvider = ({children}: LoginProviderProps) => {
  const {loggedUser, setLoggedUser} = useUserContext()
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  if (error) {
    throw error
  }

  const obtainLoginDetails = useCallback(async () => {
    try {
      const {data: loginResponse, err: loginErr} = await catchPromise(getLoginState())

      if (loginErr) {
        setError(loginErr instanceof Error ? loginErr : new Error('Login state error'))
        return
      }

      if (!loginResponse) {
        const message = 'Could not obtain login state'
        setError(new Error(message))
        return
      }

      setIsLoggedIn(loginResponse.loggedIn)

      if (!loginResponse.loggedIn || !isInitialLoad) {
        return
      }

      setIsInitialLoad(false)

      const {user_id: userId} = loginResponse.decodedToken

      const {data: userResponse, err} = await catchPromise(getUser(userId))

      if (err) {
        setError(err instanceof Error ? err : new Error('User fetch error'))
        return
      }

      if (!userResponse) {
        const message = 'Could not obtain user details'
        setError(new Error(message))
        return
      }

      setLoggedUser(userResponse.data)
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Unknown error'))
    }
  }, [isInitialLoad, setLoggedUser])

  useEffect(() => {
    if (!loggedUser) {
      void obtainLoginDetails()
    }
  }, [loggedUser, obtainLoginDetails])

  const logout = useCallback(async () => {
    setLoggedUser(null)
    setIsLoggedIn(false)
    await startLogoutProcess()
  }, [setLoggedUser])

  const memoizedLoginContextState = useMemo(
    () => ({
      logout,
      isLoggedIn
    }),
    [logout, isLoggedIn]
  )

  return <LoginContext.Provider value={memoizedLoginContextState}>{children}</LoginContext.Provider>
}
